import type { Metadata } from 'next';

export interface StrapiSeoMedia {
  url: string;
  alternativeText?: string | null;
  width?: number | null;
  height?: number | null;
}

export interface StrapiSeoRaw {
  id: number;
  metaTitle?: string | null;
  metaDescription?: string | null;
  keywords?: string | null;
  canonicalPath?: string | null;
  noIndex?: boolean | null;
  noFollow?: boolean | null;
  ogTitle?: string | null;
  ogDescription?: string | null;
  ogImage?: StrapiSeoMedia | null;
}

export interface ProcessedStrapiSeo {
  metaTitle: string | null;
  metaDescription: string | null;
  keywords: string[];
  canonicalPath: string | null;
  noIndex: boolean | null;
  noFollow: boolean | null;
  ogTitle: string | null;
  ogDescription: string | null;
  ogImageUrl: string | null;
  ogImageAlt: string | null;
}

/** @deprecated Use StrapiSeoRaw */
export type StrapiHomeSeoRaw = StrapiSeoRaw;
/** @deprecated Use ProcessedStrapiSeo */
export type ProcessedHomeSeo = ProcessedStrapiSeo;
/** @deprecated Use StrapiSeoMedia */
export type StrapiHomeSeoMedia = StrapiSeoMedia;

function ensureAbsoluteMediaUrl(url: string, baseUrl: string): string {
  if (!url.startsWith('/')) return url;
  return `${baseUrl.replace(/\/$/, '')}${url}`;
}

export function mapStrapiSeo(
  raw: StrapiSeoRaw | null | undefined,
  strapiBaseUrl = ''
): ProcessedStrapiSeo | null {
  if (!raw) return null;

  const ogImageUrl = raw.ogImage?.url
    ? ensureAbsoluteMediaUrl(raw.ogImage.url, strapiBaseUrl)
    : null;

  return {
    metaTitle: raw.metaTitle?.trim() || null,
    metaDescription: raw.metaDescription?.trim() || null,
    keywords: raw.keywords
      ? raw.keywords
          .split(',')
          .map((k) => k.trim())
          .filter(Boolean)
      : [],
    canonicalPath: raw.canonicalPath?.trim() || null,
    noIndex: raw.noIndex ?? null,
    noFollow: raw.noFollow ?? null,
    ogTitle: raw.ogTitle?.trim() || null,
    ogDescription: raw.ogDescription?.trim() || null,
    ogImageUrl,
    ogImageAlt: raw.ogImage?.alternativeText?.trim() || null,
  };
}

/** @deprecated Use mapStrapiSeo */
export const mapStrapiHomeSeo = mapStrapiSeo;

async function fetchStrapiSeoFromEndpoint(
  endpoint: string,
  populate = 'populate[SEO][populate]=*'
): Promise<StrapiSeoRaw | null> {
  const bearerToken = process.env.STRAPI_BEARER_TOKEN;
  const strapiBaseUrl = (process.env.STRAPI_BASE_URL ?? '').replace(/\/$/, '');

  if (!bearerToken || !strapiBaseUrl) {
    return null;
  }

  const response = await fetch(`${strapiBaseUrl}/api/${endpoint}?${populate}`, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${bearerToken}`,
      'Content-Type': 'application/json',
    },
    cache: 'no-store',
  });

  if (!response.ok) {
    console.error(`Strapi SEO error (${endpoint}): ${response.status}`);
    return null;
  }

  const data = await response.json();
  return (data?.data?.SEO as StrapiSeoRaw | undefined) ?? null;
}

export async function fetchStrapiHomeSeoRaw(): Promise<StrapiSeoRaw | null> {
  return fetchStrapiSeoFromEndpoint('home');
}

export async function fetchStrapiInfoContactoSeoRaw(): Promise<StrapiSeoRaw | null> {
  return fetchStrapiSeoFromEndpoint('info-contacto');
}

export async function fetchStrapiFaqSeoRaw(): Promise<StrapiSeoRaw | null> {
  return fetchStrapiSeoFromEndpoint('faq');
}

export async function getStrapiHomeSeo(): Promise<ProcessedStrapiSeo | null> {
  const strapiBaseUrl = (process.env.STRAPI_BASE_URL ?? '').replace(/\/$/, '');
  const raw = await fetchStrapiHomeSeoRaw();
  return mapStrapiSeo(raw, strapiBaseUrl);
}

export async function getStrapiInfoContactoSeo(): Promise<ProcessedStrapiSeo | null> {
  const strapiBaseUrl = (process.env.STRAPI_BASE_URL ?? '').replace(/\/$/, '');
  const raw = await fetchStrapiInfoContactoSeoRaw();
  return mapStrapiSeo(raw, strapiBaseUrl);
}

export async function getStrapiFaqSeo(): Promise<ProcessedStrapiSeo | null> {
  const strapiBaseUrl = (process.env.STRAPI_BASE_URL ?? '').replace(/\/$/, '');
  const raw = await fetchStrapiFaqSeoRaw();
  return mapStrapiSeo(raw, strapiBaseUrl);
}

export function buildMetadataFromSeo(
  seo: ProcessedStrapiSeo | null,
  fallback: Metadata
): Metadata {
  if (!seo) return fallback;

  const fallbackCanonical =
    typeof fallback.alternates?.canonical === 'string' ? fallback.alternates.canonical : undefined;

  const metadata: Metadata = {
    ...(fallback.metadataBase ? { metadataBase: fallback.metadataBase } : {}),
    ...(seo.metaTitle ? { title: seo.metaTitle } : fallback.title ? { title: fallback.title } : {}),
    ...(seo.metaDescription
      ? { description: seo.metaDescription }
      : fallback.description
        ? { description: fallback.description }
        : {}),
    ...(seo.keywords.length > 0
      ? { keywords: seo.keywords }
      : fallback.keywords
        ? { keywords: fallback.keywords }
        : {}),
    alternates: {
      canonical: seo.canonicalPath || fallbackCanonical || undefined,
    },
  };

  // Solo establece robots si el usuario configuró explícitamente al menos uno de los campos
  // (null = no configurado, boolean = configurado explícitamente)
  if (seo.noIndex !== null || seo.noFollow !== null) {
    metadata.robots = {
      index: seo.noIndex !== null ? !seo.noIndex : undefined,
      follow: seo.noFollow !== null ? !seo.noFollow : undefined,
    };
  }

  const ogTitle = seo.ogTitle || seo.metaTitle;
  const ogDescription = seo.ogDescription || seo.metaDescription;
  const fallbackOg = (fallback.openGraph as Record<string, unknown>) ?? {};

  if (ogTitle || ogDescription || seo.ogImageUrl || fallback.openGraph) {
    metadata.openGraph = {
      ...fallbackOg,
      ...(ogTitle ? { title: ogTitle } : {}),
      ...(ogDescription ? { description: ogDescription } : {}),
      // Solo sobreescribir imágenes OG si Strapi provee una explícitamente
      ...(seo.ogImageUrl
        ? {
            images: [
              {
                url: seo.ogImageUrl,
                ...(seo.ogImageAlt ? { alt: seo.ogImageAlt } : {}),
              },
            ],
          }
        : {}),
    };
  }

  return metadata;
}

const HOME_METADATA_FALLBACK: Metadata = {
  alternates: { canonical: '/' },
};

const INFO_CONTACTO_METADATA_FALLBACK: Metadata = {
  alternates: { canonical: '/atencion-al-cliente' },
};

const FAQ_METADATA_FALLBACK: Metadata = {
  alternates: { canonical: '/preguntas-frecuentes' },
};

export {
  HOME_METADATA_FALLBACK,
  INFO_CONTACTO_METADATA_FALLBACK,
  FAQ_METADATA_FALLBACK,
};

export function buildHomeMetadataFromSeo(seo: ProcessedStrapiSeo | null): Metadata {
  return buildMetadataFromSeo(seo, HOME_METADATA_FALLBACK);
}

export function buildInfoContactoMetadataFromSeo(seo: ProcessedStrapiSeo | null): Metadata {
  return buildMetadataFromSeo(seo, INFO_CONTACTO_METADATA_FALLBACK);
}

export function buildFaqMetadataFromSeo(seo: ProcessedStrapiSeo | null): Metadata {
  return buildMetadataFromSeo(seo, FAQ_METADATA_FALLBACK);
}
